import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import { useMediaQuery } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useSelector, useDispatch } from 'react-redux';
import Popup from 'reactjs-popup';
import './AdminTicketList.scss';

import { fetchAllTickets, setSelectedTicket, fetchTicketById } from '../../../../slices/TicketSlice';
import TicketDetailedView from '../../../StudentTicketDashboard/TicketDetailedView/TicketDetailedView';

/**
 * SimpleDataGrid Component
 * 
 * @component
 * @description
 * Displays a read-only table of admin tickets using MUI's 'DataGrid'. Columns include
 * User, Title, Type, Status, and Last Response. The grid is paginated and styled via 
 * 'AdminGrid.scss'. Rows in this demo are static sample data and use 'User' as the unique
 * row identifier via 'getRowId'.
 * 
 * @returns {JSX.Element} A Material UI 'Box' containing a header and a 'DataGrid' of tickets.
 * 
 * @example
 * // Usage:
 * import SimpleDataGrid from './AdminGrid';
 * 
 * function AdminTicketList() {
 *  return <SimpleDataGrid />;
 * }
 */
export const formatStatus = (statusString) => {
    if(!statusString) return "—";
    return statusString
        .replace(/_/g, ' ')
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

export default function AdminTicketList() {
    const [openDetailView, setOpenDetailView] = useState(false);
    const dispatch = useDispatch();

    const isDesktop = useMediaQuery('(min-width:1200px) and (min-height: 800px)');

    const rows = useSelector((state) => state.tickets.ticketsList);

    useEffect(() => {
        dispatch(fetchAllTickets());
    }, [dispatch]);

    const onRowClick = (params) => {
        const ticketId = params.row.id;
        dispatch(setSelectedTicket(params.row));
        dispatch(fetchTicketById(ticketId));
        setOpenDetailView(true);
    };

    const baseColumns = [
        { field: 'author', headerName: 'User', flex: 1, minWidth: 150 },
        { field: 'title', headerName: 'Title', flex: 1, minWidth: 150 },
        { field: 'description', headerName: 'Description', flex: 1, minWidth: 150 },
        { field: 'status', headerName: 'Status', flex: 1, minWidth: 150 },
        { field: 'createdAt', headerName: 'Created Date', flex: 1, minWidth: 150 },
        { field: 'assigneeName', headerName: 'Assignee', flex: 1, minWidth: 150 },
        { field: 'id', headerName: 'Ticket ID', flex: 1, minWidth: 150 }
    ];

    const columns = baseColumns.map((col) => ({
        ...col,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params) => {
            let displayValue = params.value;

            if (col.field === 'status') {
                const formattedStatus = formatStatus(params.value);
                const getStatusColor = (status) => {
                    switch (status) {
                    case 'Open':
                        return '#2b91ff';
                    case 'In Progress':
                        return '#008000';
                    case 'Closed':
                        return '#B3192B';
                    case 'Resolved':
                        return '#185595';
                    case 'Blocked':
                        return '#DE3063';
                    default:
                        return '#808080';
                    }
                };
        
                return (
                    <div
                    style={{
                        color: getStatusColor(formattedStatus),
                        fontWeight: 600,
                    }}
                    >
                    {formattedStatus}
                    </div>
                );
            } else if (col.field === 'author' && typeof params.value === 'object' && params.value !== null) {
                displayValue = `${params.value.firstName} ${params.value.lastName}`;
            } else if (col.field === 'createdAt' && params.value) {
                const rawValue = String(params.value);

                const iso = rawValue.includes("T") ? rawValue : rawValue.replace(" ", "T");
                
                const utc = iso.endsWith("Z") ? iso : iso + "Z";
                
                displayValue = new Date(utc).toLocaleString('en-US', {
                    timeZone: 'America/Chicago',
                    year: '2-digit',
                    month: 'numeric',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                });
            } else if (!params.value) {
                displayValue = "—";
            }

            return (
                <div className="truncate" title={typeof displayValue === 'string' ? displayValue : ''}>
                    {displayValue}
                </div>
            );
        }
    }));

    return (
        <Box className="AdminTicketList" data-testid="admin-ticket-list-container">
            <div className="AdminTicketList-header" data-testid="admin-ticket-list-header">
            </div>
            <Box
                sx={{
                    height: 'calc(100vh - 207px)',
                    width: '100%',
                    overflow: 'hidden',
                }}>
                <DataGrid
                    rows={rows ?? []}
                    columns={columns}
                    getRowId={(row) => row.id}
                    onRowClick={onRowClick}
                    autoPageSize
                    pageSizeOptions={isDesktop ? [10] : []}
                    initialState={isDesktop ? {pagination: { paginationModel: { pageSize: 10 } } } : undefined }
                    rowHeight={60}
                    columnHeaderHeight={48}
                    data-testid="admin-ticket-data-grid"
                    sx={{
                        border: 'none',
                        fontFamily: "'Poppins', sans-serif",
                        '& .MuiDataGrid-virtualScroller': { overflowY: 'hidden' },
                        '& .MuiDataGrid-footerContainer': { padding: '0px !important' },
                        '& .MuiTablePagination-displayedRows': {
                            fontFamily: "'Poppins', sans-serif",
                        },
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-select': {
                            fontFamily: "'Poppins', sans-serif",
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontFamily: "'Poppins', sans-serif",
                            fontWeight: 600,
                        },
                        '& .MuiDataGrid-cell': {
                            fontFamily: "'Poppins', sans-serif",
                         },
                    }}
                    />
                </Box>

                <div className="TicketDetailedViewPopup">
                    <Popup
                        open={openDetailView}
                        onClose={() => setOpenDetailView(false)}
                        modal
                        nested
                        data-testid="ticket-detailed-view-popup"
                        >
                        <TicketDetailedView datatestid="ticket-detailed-view"
                            onClose={() => setOpenDetailView(false)}
                        />
                    </Popup>
                </div>
            </Box>
    );
}